/**
 * Recovery from a stale bundle.
 *
 * Every deploy publishes fresh asset filenames — Vite hashes them by content,
 * so `Dashboard-Dk-uB2Wn.js` becomes something else entirely. A tab left open
 * across a deploy still holds the old index.html, so the moment the operator
 * navigates to a lazy route it asks for a chunk that no longer exists on the
 * server and the import fails: "Failed to fetch dynamically imported module".
 *
 * Nothing is broken — the tab is simply a version behind. Reloading fetches
 * the new index.html and, with it, the new chunk names, so the recovery is to
 * reload rather than to make the operator read an error and click a button.
 *
 * The reload is guarded. A chunk can also be missing because a deploy went out
 * half-finished, and reloading would then loop forever. One attempt per short
 * window means a genuine outage falls through to the error boundary instead.
 */

const GUARD_KEY = "wms:stale-bundle-reload";

/** Long enough to cover a slow reload; short enough that a later deploy still recovers. */
const GUARD_WINDOW_MS = 30_000;

/** Browsers word this failure differently; match the family, not one phrasing. */
const STALE_BUNDLE_PATTERN =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror|dynamically imported module/i;

export function isStaleBundleError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return STALE_BUNDLE_PATTERN.test(message);
}

/** Read the guard. A browser that refuses sessionStorage just gets one attempt. */
function lastAttempt(): number {
  try {
    return Number(sessionStorage.getItem(GUARD_KEY) ?? 0);
  } catch {
    return 0;
  }
}

/**
 * Reload once to pick up the new bundle.
 *
 * @returns whether a reload was started — false means one was already tried,
 *   and the caller should surface the error instead.
 */
export function recoverFromStaleBundle(): boolean {
  if (Date.now() - lastAttempt() < GUARD_WINDOW_MS) return false;

  try {
    sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch {
    // Nothing to do — without the guard the error boundary is the backstop.
  }

  window.location.reload();
  return true;
}

/**
 * Vite fires `vite:preloadError` when a preloaded chunk 404s, which is the
 * usual way a stale tab finds out. Handling it here catches the failure
 * before it reaches a boundary; calling preventDefault stops Vite throwing
 * the error on as well.
 */
export function installStaleBundleRecovery(): void {
  window.addEventListener("vite:preloadError", ((event: Event) => {
    event.preventDefault();
    recoverFromStaleBundle();
  }) as EventListener);
}
